import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import { useRef } from "react";
import Reveal from "../components/Reveal";
import { companies } from "../data/companies";

const metrics = (c) => [
  { value: c.followers, label: "Followers" },
  { value: c.founded, label: "Founded" },
  { value: c.platform, label: "Platform" },
];

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const company = companies.find((c) => c.id === id);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  useEffect(() => {
    if (!company) navigate("/portfolio", { replace: true });
  }, [company, navigate]);

  if (!company) return null;

  return (
    <div data-testid={`company-detail-${company.id}`} className="bg-[#070707]">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative flex min-h-[80vh] items-end overflow-hidden px-6 pb-16 pt-40 md:px-12 md:pb-24"
      >
        <motion.div style={{ y: imgY }} className="absolute inset-0 z-0">
          <img
            src={company.image}
            alt={company.name}
            className="h-full w-full object-cover grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/70 to-[#070707]/40" />
        </motion.div>

        <div className="relative z-10 mx-auto w-full max-w-[1600px]">
          <Link
            to="/portfolio"
            data-testid="detail-back-link"
            className="link-sweep mb-10 inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#F5F5F0]"
          >
            <ArrowLeft size={14} /> Back to Portfolio
          </Link>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#E83D16]"
          >
            {company.category} &mdash; {company.platform}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
            className="mt-4 font-display text-[16vw] leading-[0.86] tracking-tighter text-[#F5F5F0] md:text-[10vw]"
          >
            {company.name}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.8 }}
            className="mt-6 max-w-xl font-display text-xl italic text-[#A1A1AA] md:text-2xl"
          >
            {company.tagline}
          </motion.p>
        </div>
      </section>

      {/* METRICS BAR */}
      <section className="border-y border-[#27272A] bg-[#070707] px-6 py-12 md:px-12">
        <div className="mx-auto grid max-w-[1600px] grid-cols-3 gap-8">
          {metrics(company).map((m, i) => (
            <Reveal key={m.label} delay={i * 0.08}>
              <p className="font-display text-4xl tracking-tight text-[#F5F5F0] md:text-6xl">
                {m.value}
              </p>
              <p className="mt-2 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] md:text-xs">
                {m.label}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BODY */}
      <section className="px-6 py-24 md:px-12 md:py-36">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 md:grid-cols-12">
          <Reveal className="md:col-span-4">
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#E83D16]">
              [ Overview ]
            </p>
          </Reveal>
          <div className="md:col-span-7 md:col-start-6">
            <Reveal>
              <p className="font-display text-2xl leading-snug text-[#F5F5F0] md:text-3xl">
                {company.description}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {company.highlights.map((h) => (
                  <li
                    key={h}
                    className="flex items-start gap-3 border-t border-[#27272A] pt-4 font-body text-sm text-[#F5F5F0]"
                  >
                    <Check size={16} className="mt-0.5 shrink-0 text-[#E83D16]" />
                    {h}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.2}>
              <a
                href={company.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`detail-visit-${company.id}`}
                className="btn-editorial group mt-14 inline-flex items-center gap-3 border border-[#F5F5F0] px-10 py-5 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#F5F5F0] hover:text-white"
              >
                Visit {company.name} on {company.platform}
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </a>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
